import EsFlag from "@/assets/icons/languages/es";
import GBFlag from "@/assets/icons/languages/gb";
import ItFlag from "@/assets/icons/languages/it";
import { FrFlag } from "@/assets/icons/languages/fr";
import { PlFlag } from "@/assets/icons/languages/pl";
import useOutsideClick from "@/hooks/useOutsideClick";
import i18n from "@/localization/i18n";
import { ReactSVGIcon } from "@/types/common.type";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Styled from "./style";

export interface ILanguageConfig {
  id: number | string;
  name: string;
  code: string;
  icon: ReactSVGIcon | null;
  abbr: string;
}

const LanguageSelector = () => {
  const { t } = useTranslation();

  const languageConfig = [
    {
      id: 1,
      name: t("LANGUAGE.ENGLISH"),
      code: "en",
      icon: GBFlag,
      abbr: "EN"
    },
    {
      id: 2,
      name: t("LANGUAGE.ITALIAN"),
      code: "it",
      icon: ItFlag,
      abbr: "IT"
    },
    {
      id: 3,
      name: t("LANGUAGE.SPANISH"),
      code: "es",
      icon: EsFlag,
      abbr: "ES"
    },
    {
      id: 4,
      name: t("LANGUAGE.FRENCH"),
      code: "fr",
      icon: FrFlag,
      abbr: "FR"
    },
    {
      id: 5,
      name: t("LANGUAGE.POLISH"),
      code: "pl",
      icon: PlFlag,
      abbr: "PL"
    }
  ];

  const initialLanguage = localStorage.getItem("lang_code") || "en";
  const [active, setActive] = useState<string>(
    languageConfig.find((lang) => lang.code === initialLanguage).code
  );
  const [open, setOpen] = useState(false);

  const languageSelectorRef = useRef(null);

  const changeHandler = async (code: string) => {
    await i18n.changeLanguage(code);
    localStorage.setItem("lang_code", code);
    setOpen(false);
    setActive(code);
  };

  const onClickHandler = () => setOpen(!open);

  const outsideContainerRef = useRef<HTMLDivElement>(null);
  useOutsideClick(outsideContainerRef, () => setOpen(false));

  const activeLanguage = languageConfig.find((lang) => lang.code === active);

  return (
    <Styled.Container ref={outsideContainerRef}>
      <Styled.ActiveLanguageWrapper
        onClick={onClickHandler}
        ref={languageSelectorRef}
      >
        {activeLanguage?.icon && <activeLanguage.icon />}
        <Styled.ActiveLanguage data-testid="active_lang">
          {activeLanguage?.name}
        </Styled.ActiveLanguage>
        <Styled.ArrowDownIcon open={open} />
      </Styled.ActiveLanguageWrapper>
      <Styled.LanguageList open={open}>
        {languageConfig.map(({ id, code, name, icon: FlagIcon }) => (
          <Styled.LanguageItem
            key={id}
            onClick={() => changeHandler(code)}
            data-testid="lang_item"
          >
            <Styled.ItemWrapper bold={activeLanguage?.id === id}>
              <Styled.IconWrapper>
                {FlagIcon && <FlagIcon />}
              </Styled.IconWrapper>
              {name}
            </Styled.ItemWrapper>
            {activeLanguage?.id === id && <Styled.CheckLanguageIcon />}
          </Styled.LanguageItem>
        ))}
      </Styled.LanguageList>
    </Styled.Container>
  );
};

export { LanguageSelector };
